export const COMMUNITY_IMAGE_MAX_BYTES = 10 * 1024 * 1024 - 1;
export const RE_ENCODE_TARGET_BYTES = 9 * 1024 * 1024;
export const CAROUSEL_MAX_LONG_EDGE = 1920;
export const ICON_MAX_LONG_EDGE = 512;

const JPEG_QUALITIES = [0.85, 0.7];

export class ImageTooLargeError extends Error {
  constructor(
    readonly imageBytes: number,
    readonly targetBytes: number,
  ) {
    super(`Image of ${imageBytes} bytes could not be re-encoded under ${targetBytes} bytes`);
    this.name = 'ImageTooLargeError';
  }
}

interface ShrinkOptions {
  maxBytes?: number;
  targetBytes?: number;
  maxLongEdge?: number;
  preserveAlpha?: boolean;
}

interface Size {
  width: number;
  height: number;
}

export const computeTargetSize = (width: number, height: number, maxLongEdge: number): Size => {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxLongEdge) return { width, height };

  const scale = maxLongEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const readImageSize = (file: File): Promise<Size> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the image to resize it'));
    };
    probe.src = url;
  });

const encode = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

export async function shrinkImageToFit(file: File, options: ShrinkOptions = {}): Promise<File> {
  const {
    maxBytes = COMMUNITY_IMAGE_MAX_BYTES,
    targetBytes = RE_ENCODE_TARGET_BYTES,
    maxLongEdge = CAROUSEL_MAX_LONG_EDGE,
    preserveAlpha = false,
  } = options;

  if (file.size <= maxBytes) return file;

  const source = await readImageSize(file);
  const target = computeTargetSize(source.width, source.height, maxLongEdge);
  const bitmap = await createImageBitmap(file, {
    resizeWidth: target.width,
    resizeHeight: target.height,
    resizeQuality: 'high',
    imageOrientation: 'from-image',
  });

  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Could not get a 2d context to resize the image');
  }
  if (!preserveAlpha) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const type = preserveAlpha ? 'image/png' : 'image/jpeg';
  for (const quality of preserveAlpha ? [undefined] : JPEG_QUALITIES) {
    const blob = await encode(canvas, type, quality);
    if (blob && blob.size <= targetBytes) {
      return new File([blob], file.name, { type, lastModified: file.lastModified });
    }
  }

  throw new ImageTooLargeError(file.size, targetBytes);
}
